// Supabase client for admin dashboard
import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: window.localStorage,
  },
});

export const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4500';

// Thrown by apiRequest on any non-2xx or network failure. `status` is 0 for a
// transport-level failure (offline/DNS/CORS — fetch() rejected before a response
// existed) so callers can distinguish "the network is down" from "the server said
// no" (401/403 unauthorized, 429 rate-limited, other 4xx/5xx) — the 8-state
// coverage (Law 8 / 18H §1.4) needs this signal and a bare Error message doesn't
// carry it.
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${apiUrl}/api/v1${path}`, {
      ...options,
      headers,
    });
  } catch (err) {
    // fetch() itself rejected — offline, DNS failure, CORS, connection refused.
    // status=0 is the offline-state signal (never a real HTTP code).
    throw new ApiError(err instanceof Error ? err.message : 'Network request failed', 0);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new ApiError(error.error?.message || 'Request failed', response.status);
  }

  // 204 No Content (e.g. DELETE /api-keys/:id) has no body: response.json() would
  // throw "Unexpected end of JSON input" on a SUCCESSFUL call, which made every
  // caller either swallow all errors or report a success as a failure.
  if (response.status === 204) return undefined as T;

  return response.json() as Promise<T>;
}
